import {
  faArrowRight,
  faCheckCircle,
  faCircle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button, Modal, Spinner } from '@themesberg/react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pushWondersoftInternalTransfer } from '../../parse-functions/trips';

const STEP_PENDING = 'pending';
const STEP_RUNNING = 'running';
const STEP_SUCCESS = 'success';
const STEP_FAILED = 'failed';

const StepStatusIcon = ({ status }) => {
  if (status === STEP_RUNNING) {
    return <Spinner animation="border" size="sm" className="text-info" />;
  }
  if (status === STEP_SUCCESS) {
    return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
  }
  if (status === STEP_FAILED) {
    return <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />;
  }
  return <FontAwesomeIcon icon={faCircle} className="text-gray-400" />;
};

const WondersoftTransferStepsModal = (props) => {
  const {
    showModal,
    closeModal,
    transfers = [],
    tripNumber,
    warehouseCode,
    onCloseTrip,
    onCompleted,
  } = props;
  const { t } = useTranslation();
  const [stepStates, setStepStates] = useState([]);
  const [stepErrors, setStepErrors] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const stepStatesRef = useRef([]);

  const totalSteps = transfers.length + 1;

  const updateStepState = (index, status) => {
    stepStatesRef.current = stepStatesRef.current.map((item, itemIndex) =>
      itemIndex === index ? status : item
    );
    setStepStates(stepStatesRef.current);
  };

  const runSteps = async () => {
    setIsRunning(true);
    setStepErrors({});
    try {
      for (let index = 0; index < transfers.length; index += 1) {
        if (stepStatesRef.current[index] === STEP_SUCCESS) {
          // already pushed in a previous attempt, backend skips it too
          continue;
        }
        updateStepState(index, STEP_RUNNING);
        try {
          await pushWondersoftInternalTransfer(
            tripNumber,
            warehouseCode,
            transfers[index].parcelObjectId
          );
          updateStepState(index, STEP_SUCCESS);
        } catch (e) {
          updateStepState(index, STEP_FAILED);
          setStepErrors({ [index]: e?.message || t('Wondersoft update failed') });
          return;
        }
      }
      const closeTripIndex = transfers.length;
      updateStepState(closeTripIndex, STEP_RUNNING);
      try {
        await onCloseTrip();
        updateStepState(closeTripIndex, STEP_SUCCESS);
        onCompleted();
      } catch (e) {
        updateStepState(closeTripIndex, STEP_FAILED);
        setStepErrors({
          [closeTripIndex]: e?.message || t('Trip Closure Failed'),
        });
      }
    } finally {
      setIsRunning(false);
    }
  };

  useEffect(() => {
    if (showModal) {
      stepStatesRef.current = new Array(totalSteps).fill(STEP_PENDING);
      setStepStates(stepStatesRef.current);
      setStepErrors({});
      runSteps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const hasFailure = stepStates.indexOf(STEP_FAILED) > -1;

  return (
    <Modal
      show={showModal}
      onHide={() => {
        if (!isRunning) {
          closeModal();
        }
      }}
      backdrop="static"
      keyboard={false}
      size="lg"
      centered
    >
      <Modal.Header>
        <Modal.Title className="h5">
          {t('Updating Wondersoft Inventory')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-gray-600">
          {t(
            'Moving replaced / defective items between Wondersoft bins before closing the trip. Please do not close this window.'
          )}
        </p>
        {transfers.map((transfer, index) => (
          <div
            key={transfer.parcelObjectId}
            className="d-flex align-items-start border-bottom py-2"
          >
            <div className="me-3 pt-1">
              <StepStatusIcon status={stepStates[index]} />
            </div>
            <div className="flex-grow-1">
              <div className="fw-bold">
                {transfer.product || transfer.productCode}
                {transfer.serialNo ? ` — ${transfer.serialNo}` : ''}
              </div>
              <div className="small text-gray-600">
                {transfer.invoiceNo}
                {transfer.customerName ? ` · ${transfer.customerName}` : ''}
              </div>
              <div className="small">
                <Badge bg="light" text="dark">
                  {transfer.fromBin}
                </Badge>
                <FontAwesomeIcon icon={faArrowRight} className="mx-2" />
                <Badge bg="light" text="dark">
                  {transfer.toBin}
                </Badge>
              </div>
              {stepErrors[index] && (
                <div className="small text-danger mt-1">{stepErrors[index]}</div>
              )}
            </div>
          </div>
        ))}
        <div className="d-flex align-items-start py-2">
          <div className="me-3 pt-1">
            <StepStatusIcon status={stepStates[transfers.length]} />
          </div>
          <div className="flex-grow-1">
            <div className="fw-bold">{t('Close Trip')}</div>
            {stepErrors[transfers.length] && (
              <div className="small text-danger mt-1">
                {stepErrors[transfers.length]}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="link"
          className="text-gray ms-auto"
          disabled={isRunning}
          onClick={closeModal}
        >
          {t('Close')}
        </Button>
        {hasFailure && (
          <Button variant="secondary" disabled={isRunning} onClick={runSteps}>
            {t('Retry Failed Steps')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default WondersoftTransferStepsModal;
